import React from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AIMessageInput } from '../AIMessageInput';
import { cn } from '@/lib/core/utils';

interface MobileChatInputAreaProps {
  onSend: (message: string, attachedFile?: import('@/lib/ai-chat/file-parser').ParsedFileData) => void;
  onStop: () => void;
  disabled: boolean;
  isLoading: boolean;
  placeholder?: string;
  error?: string | null;
  canRetry?: boolean;
  onRetry?: () => void;
  className?: string;
  allowFileUpload?: boolean;
}

/**
 * Input wrapper component with keyboard positioning and safe area padding.
 * Wraps AIMessageInput with proper mobile handling.
 */
export const MobileChatInputArea: React.FC<MobileChatInputAreaProps> = ({
  onSend,
  onStop,
  disabled,
  isLoading,
  placeholder = 'Ask, search or make anything...',
  error,
  canRetry,
  onRetry,
  className,
  allowFileUpload = false,
}) => {
  return (
    <div className={cn(
      'shrink-0 border-t bg-background',
      // Safe area bottom padding for iOS home indicator
      'pb-[env(safe-area-inset-bottom)]',
      className
    )}>
      {/* Error display with retry */}
      {(error || canRetry) && (
        <div className="px-4 py-2 bg-destructive/10 text-destructive text-sm flex items-center justify-between gap-2 shrink-0">
          <span>{error || 'Generation stopped'}</span>
          {canRetry && onRetry && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRetry}
              className="h-7 px-2 text-xs"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          )}
        </div>
      )}

      <AIMessageInput
        onSend={onSend}
        onStop={onStop}
        disabled={disabled}
        isLoading={isLoading}
        placeholder={placeholder}
        autoFocus={false}
        allowFileUpload={allowFileUpload}
      />
    </div>
  );
};
